import React from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  displayStatus?: string;
  loading: boolean;
  timedOut: boolean;
  hasError: boolean;
  hasValidContinuation: boolean;
  redirecting?: boolean;
  onRefresh: () => void;
}

const messages: Record<string, { title: string; detail: string; error?: boolean }> = {
  WaitingForPayment: {
    title: 'Waiting for payment confirmation…',
    detail: 'Please keep this page open while Xendit confirms your payment.',
  },
  WaitingForAuthorization: {
    title: 'Waiting for authorization confirmation…',
    detail: 'Please keep this page open while Xendit confirms your card hold.',
  },
  Processing: {
    title: 'Payment received. Completing check-in…',
    detail: 'Do not make another payment. Your check-in is being completed.',
  },
  Completed: {
    title: 'Payment and check-in completed.',
    detail: 'Your confirmation details are being loaded.',
  },
  Authorized: {
    title: 'Card hold authorized.',
    detail: 'Your pre-authorization is confirmed. Loading your confirmation details.',
  },
  PaymentReceivedActionRequired: {
    title: 'Payment received, but check-in requires assistance.',
    detail: 'Do not pay again. Please contact the hotel and provide your booking details.',
    error: true,
  },
  AuthorizationReceivedActionRequired: {
    title: 'Authorization received, but check-in requires assistance.',
    detail: 'Do not authorize again. Please contact the hotel and provide your booking details.',
    error: true,
  },
  Failed: {
    title: 'Payment was not completed.',
    detail: 'Please return to the payment step or contact the hotel for assistance.',
    error: true,
  },
  Cancelled: {
    title: 'Payment was cancelled.',
    detail: 'You may return to the payment step when you are ready.',
    error: true,
  },
  Expired: {
    title: 'The payment session expired.',
    detail: 'Please return to the payment step to start a new session.',
    error: true,
  },
};

export const XenditPaymentStatus = ({
  displayStatus,
  loading,
  timedOut,
  hasError,
  hasValidContinuation,
  redirecting = false,
  onRefresh,
}: Props) => {
  const invalid = !hasValidContinuation;
  const queryProblem = hasError || (!loading && hasValidContinuation && !displayStatus);

  const baseState = invalid
    ? {
        title: 'Payment status cannot be verified.',
        detail:
          'Please return using the same browser or contact the hotel. Do not make another payment if funds were deducted.',
        error: true,
      }
    : queryProblem
      ? {
          title: 'Unable to load payment status.',
          detail: 'Please refresh the status or contact the hotel if the problem continues.',
          error: true,
        }
      : messages[displayStatus || 'WaitingForPayment'];

  const state = redirecting
    ? {
        ...baseState,
        title: baseState.title,
        detail: 'Please wait while we redirect you…',
        error: false,
      }
    : baseState;

  const successIcon =
    !redirecting && (displayStatus === 'Completed' || displayStatus === 'Authorized');
  const showSpinner =
    redirecting || (loading && !displayStatus) || (!state.error && !successIcon);

  return (
    <Card sx={{ borderRadius: '10px', mt: 2 }}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        {showSpinner ? (
          <CircularProgress size={40} sx={{ mb: 2 }} />
        ) : state.error ? (
          <ErrorOutlineIcon color="warning" sx={{ fontSize: 44, mb: 1 }} />
        ) : (
          <TaskAltIcon color="success" sx={{ fontSize: 44, mb: 1 }} />
        )}

        <Typography fontSize="18px" fontWeight={700}>
          {state.title}
        </Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mt: 1 }}>
          {state.detail}
        </Typography>

        {timedOut && !state.error && !redirecting && (
          <Typography fontSize="13px" color="text.secondary" sx={{ mt: 2 }}>
            Automatic updates have paused. Your payment may still be processing.
          </Typography>
        )}

        {(timedOut || state.error) &&
          hasValidContinuation &&
          !redirecting &&
          displayStatus !== 'Completed' &&
          displayStatus !== 'Authorized' && (
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={onRefresh} disabled={loading}>
                Refresh Status
              </Button>
            </Box>
          )}
      </CardContent>
    </Card>
  );
};
